import React, { useState } from 'react';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { mockParserApi } from '../../services/mockParserApi';

const EXAM_OPTIONS = ['CUET'];
const PAPER_OPTIONS = {
  CUET: [
    { label: 'General Test', paperType: 'general-aptitude', subject: 'General Knowledge' },
    { label: 'English', paperType: 'language', subject: 'English' },
    { label: 'Hindi', paperType: 'language', subject: 'Hindi' },
    { label: 'Mathematics', paperType: 'domain', subject: 'Mathematics' },
    { label: 'Physics', paperType: 'domain', subject: 'Physics' },
    { label: 'Chemistry', paperType: 'domain', subject: 'Chemistry' },
    { label: 'Biology', paperType: 'domain', subject: 'Biology' },
  ],
};

export default function UploadPYQForm({ onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    sourceExam: 'CUET',
    sourceYear: new Date().getFullYear(),
    selectedPaper: 0, // Index in PAPER_OPTIONS array
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === 'selectedPaper') {
      processedValue = parseInt(value, 10);
    } else if (name === 'sourceYear') {
      processedValue = parseInt(value, 10);
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
      // Reset paper when exam changes
      ...(name === 'sourceExam' && { selectedPaper: 0 }),
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Please select a PDF file');
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        toast.error('File size must be less than 50MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      toast.error('Please select a PDF file');
      return;
    }

    setUploading(true);
    const uploadToast = toast.loading('Uploading and parsing PDF...');

    try {
      const selectedPaperConfig = PAPER_OPTIONS[formData.sourceExam][formData.selectedPaper];
      
      const result = await mockParserApi.uploadPYQ(selectedFile, {
        sourceExam: formData.sourceExam,
        sourceYear: parseInt(formData.sourceYear, 10),
        paperType: selectedPaperConfig.paperType,
        subject: selectedPaperConfig.subject,
      });

      // Handle new API response format
      const count = result.data?.questionsCount || result.questionsCount || 0;
      const skipped = result.data?.duplicatesSkipped || result.duplicatesSkipped || 0;
      
      let message = `Successfully parsed ${count} questions!`;
      if (skipped > 0) {
        message += ` (${skipped} duplicates skipped)`;
      }

      toast.success(message, { id: uploadToast });

      // Reset form
      setSelectedFile(null);
      setFormData({
        sourceExam: 'CUET',
        sourceYear: new Date().getFullYear(),
        selectedPaper: 0,
      });
      document.getElementById('pdf-upload').value = '';

      // Notify parent component
      if (onUploadSuccess) {
        onUploadSuccess(result);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload PDF', { id: uploadToast });
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center mb-4">
        <CloudArrowUpIcon className="h-6 w-6 text-indigo-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">
          Upload PYQ Paper
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Exam Selection */}
          <div>
            <label
              htmlFor="sourceExam"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Exam
            </label>
            <select
              id="sourceExam"
              name="sourceExam"
              value={formData.sourceExam}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={uploading}
            >
              {EXAM_OPTIONS.map((exam) => (
                <option key={exam} value={exam}>
                  {exam}
                </option>
              ))}
            </select>
          </div>

          {/* Year Selection */}
          <div>
            <label
              htmlFor="sourceYear"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Year
            </label>
            <input
              type="number"
              id="sourceYear"
              name="sourceYear"
              value={formData.sourceYear}
              onChange={handleInputChange}
              min="2000"
              max={new Date().getFullYear() + 1}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={uploading}
            />
          </div>

          {/* Paper Selection */}
          <div>
            <label
              htmlFor="selectedPaper"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Paper Type
            </label>
            <select
              id="selectedPaper"
              name="selectedPaper"
              value={formData.selectedPaper}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={uploading}
            >
              {PAPER_OPTIONS[formData.sourceExam].map((paper, index) => (
                <option key={index} value={index}>
                  {paper.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* File Upload */}
        <div>
          <label
            htmlFor="pdf-upload"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            PDF File
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="file"
              id="pdf-upload"
              accept=".pdf"
              onChange={handleFileSelect}
              className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              disabled={uploading}
            />
            <button
              type="submit"
              disabled={uploading || !selectedFile}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
          {selectedFile && (
            <p className="mt-2 text-sm text-gray-600">
              Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
            </p>
          )}
        </div>

        <p className="text-xs text-gray-500">
          * Maximum file size: 50MB. The PDF will be parsed using AI to extract questions.
        </p>
      </form>
    </div>
  );
}
